import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";

interface PrefetchOptions {
    threshold?: number;
    rootMargin?: string;
    delay?: number;
    cacheFor?: string;
    priority?: "high" | "low" | "auto";
}

interface PrefetchState {
    isPrefetched: boolean;
    isInView: boolean;
    isPrefetching: boolean;
}

/**
 * Smart prefetch hook that prefetches content when it's likely to be needed
 * Uses Intersection Observer to detect when links are in viewport
 */
export const useSmartPrefetch = (
    href: string,
    options: PrefetchOptions = {},
) => {
    const {
        threshold = 0.1,
        rootMargin = "50px",
        delay = 100,
        cacheFor = "10s",
        priority = "auto",
    } = options;

    const [state, setState] = useState<PrefetchState>({
        isPrefetched: false,
        isInView: false,
        isPrefetching: false,
    });

    const elementRef = useRef<HTMLElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();
    const observerRef = useRef<IntersectionObserver>();

    useEffect(() => {
        if (!elementRef.current || state.isPrefetched) return;

        // Create intersection observer
        observerRef.current = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;

                if (
                    entry.isIntersecting &&
                    !state.isPrefetched &&
                    !state.isPrefetching
                ) {
                    setState((prev) => ({ ...prev, isInView: true }));

                    // Add delay before prefetching to avoid prefetching on quick scrolls
                    timeoutRef.current = setTimeout(() => {
                        prefetchContent();
                    }, delay);
                } else if (!entry.isIntersecting) {
                    setState((prev) => ({ ...prev, isInView: false }));

                    // Clear timeout if element goes out of view quickly
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                }
            },
            {
                threshold,
                rootMargin,
            },
        );

        observerRef.current.observe(elementRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [href, threshold, rootMargin, delay, state.isPrefetched]);

    const prefetchContent = async () => {
        if (state.isPrefetched || state.isPrefetching) return;

        setState((prev) => ({ ...prev, isPrefetching: true }));

        // Starting prefetch - no logging needed

        try {
            // Use native browser prefetch directly due to Inertia.js compatibility issues
            // This is more reliable and avoids the destructured parameter error
            const link = document.createElement("link");
            link.rel = "prefetch";
            link.href = href;
            link.as = "document";
            document.head.appendChild(link);

            // Success - no logging needed

            setState((prev) => ({
                ...prev,
                isPrefetched: true,
                isPrefetching: false,
            }));
        } catch (error) {
            setState((prev) => ({
                ...prev,
                isPrefetching: false,
            }));
            console.warn(`❌ Prefetch error: ${href}`);
        }
    };

    // Manual prefetch trigger
    const triggerPrefetch = () => {
        if (!state.isPrefetched && !state.isPrefetching) {
            prefetchContent();
        }
    };

    return {
        elementRef,
        ...state,
        triggerPrefetch,
    };
};

/**
 * Hook for prefetching on hover with debounce
 */
export const useHoverPrefetch = (
    href: string,
    options: { delay?: number; cacheFor?: string } = {},
) => {
    const { delay = 200, cacheFor = "10s" } = options;
    const [isPrefetched, setIsPrefetched] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const handleMouseEnter = () => {
        if (isPrefetched) return;

        timeoutRef.current = setTimeout(async () => {
            try {
                // Use native browser prefetch directly
                const link = document.createElement("link");
                link.rel = "prefetch";
                link.href = href;
                link.as = "document";
                document.head.appendChild(link);

                // Success - no logging needed
                setIsPrefetched(true);
            } catch (error) {
                console.warn(`❌ Hover prefetch failed: ${href}`);
            }
        }, delay);
    };

    const handleMouseLeave = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return {
        isPrefetched,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
    };
};

/**
 * Hook for intelligent prefetch based on user behavior
 */
export const useIntelligentPrefetch = (
    links: string[],
    options: { maxConcurrent?: number; cacheFor?: string } = {},
) => {
    const { maxConcurrent = 3, cacheFor = "10s" } = options;
    const [prefetchedLinks, setPrefetchedLinks] = useState<Set<string>>(
        new Set(),
    );
    const [prefetchingLinks, setPrefetchingLinks] = useState<Set<string>>(
        new Set(),
    );

    const prefetchLink = async (href: string) => {
        if (prefetchedLinks.has(href) || prefetchingLinks.has(href)) return;
        if (prefetchingLinks.size >= maxConcurrent) return;

        setPrefetchingLinks((prev) => new Set([...prev, href]));

        try {
            // Use native browser prefetch directly
            const link = document.createElement("link");
            link.rel = "prefetch";
            link.href = href;
            link.as = "document";
            document.head.appendChild(link);

            // Success - no logging needed
            setPrefetchedLinks((prev) => new Set([...prev, href]));
        } catch (error) {
            console.warn(`❌ Intelligent prefetch failed: ${href}`);
        } finally {
            setPrefetchingLinks((prev) => {
                const newSet = new Set(prev);
                newSet.delete(href);
                return newSet;
            });
        }
    };

    const prefetchNext = (currentIndex: number) => {
        // Prefetch next 2-3 items
        const nextLinks = links.slice(currentIndex + 1, currentIndex + 4);
        nextLinks.forEach((link) => prefetchLink(link));
    };

    return {
        prefetchedLinks,
        prefetchingLinks,
        prefetchLink,
        prefetchNext,
    };
};
